import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Search, Facebook, MessageCircle, CheckCircle2, AlertTriangle, XCircle, Wand2, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { toast } from "sonner";
import SeoScore from "./SeoScore";
import ContentSeoSection from "./ContentSeoSection";

const EMPTY = { auto: true, title: "", meta_description: "", canonical_url: "", og_title: "", og_description: "", og_image: "", twitter_image: "", index: true, follow: true, focus_keyword: "", image_alt: "" };

function StatusIcon({ status }) {
  if (status === "pass") return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0" />;
  return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
}

export default function SeoTab({ entityType = "product", entity = {}, value, onChange, slug, onSlugChange }) {
  const seo = { ...EMPTY, ...(value || {}) };
  const auto = seo.auto !== false;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggest, setAiSuggest] = useState(null);
  const timer = useRef(null);

  const set = (patch) => onChange({ ...seo, ...patch });

  const draftEntity = useMemo(() => ({ ...entity, slug: slug || entity.slug, seo }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(entity), slug, JSON.stringify(seo)]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      api.post("/admin/seo/preview", { entity_type: entityType, entity: draftEntity })
        .then(({ data }) => setPreview(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [entityType, JSON.stringify(draftEntity)]);

  const runAi = async () => {
    setAiLoading(true);
    setAiSuggest(null);
    try {
      const { data } = await api.post("/admin/seo/generate", { entity_type: entityType, entity: draftEntity });
      if (data.ok) { setAiSuggest(data.suggestions); setAiOpen(true); }
      else toast.error(data.error || "AI generation failed.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const applyAi = () => {
    const hasManual = !auto && (seo.title || seo.meta_description || seo.og_title || seo.og_description);
    if (hasManual && !window.confirm("This will overwrite your current manual SEO fields with the AI suggestions. Continue?")) return;
    set({
      auto: false,
      title: aiSuggest.title || seo.title,
      meta_description: aiSuggest.meta_description || seo.meta_description,
      og_title: aiSuggest.og_title || seo.og_title,
      og_description: aiSuggest.og_description || seo.og_description,
      image_alt: aiSuggest.image_alt || seo.image_alt,
      focus_keyword: aiSuggest.focus_keyword || seo.focus_keyword,
    });
    setAiOpen(false);
    toast.success("AI suggestions applied. You can still edit them below.");
  };

  const sc = preview?.score;
  const p = preview?.seo || {};
  const g = preview?.google || {};
  const fb = preview?.facebook || {};

  return (
    <div className="space-y-6" data-testid="seo-tab">
      <Tabs defaultValue="technical">
        <TabsList>
          <TabsTrigger value="technical" data-testid="seo-subtab-technical">Technical SEO</TabsTrigger>
          <TabsTrigger value="content" data-testid="seo-subtab-content">Content SEO</TabsTrigger>
        </TabsList>
        <TabsContent value="technical" className="space-y-6 mt-4">
      {/* Header: auto toggle + AI + score */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border border-white/10 bg-white/5">
        <div className="flex items-center gap-3">
          <Switch checked={auto} onCheckedChange={(v) => set({ auto: v })} data-testid="seo-auto-toggle" />
          <div>
            <Label className="text-sm font-medium">Use Automatic SEO</Label>
            <p className="text-xs text-white/50">{auto ? "SEO is generated automatically from your templates." : "Manual overrides are active. Blank fields fall back to auto."}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {sc != null && <SeoScore score={sc.score} />}
          <Button type="button" variant="outline" className="gap-2 border-gold/40 text-gold hover:bg-gold/10" onClick={runAi} disabled={aiLoading} data-testid="seo-ai-generate-btn">
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Generate with AI
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editable fields */}
        <div className="space-y-4">
          {onSlugChange && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/60">URL Slug</Label>
              <Input value={slug || ""} onChange={(e) => onSlugChange(e.target.value)} placeholder="auto-from-name" data-testid="seo-slug-input" />
              <p className="text-[11px] text-white/40 mt-1">Changing the slug creates an automatic 301 redirect from the old URL.</p>
            </div>
          )}
          {!auto ? (
            <>
              <Field label="SEO Title" hint={`${(seo.title || "").length} chars · aim 30-60`}>
                <Input value={seo.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder={p.title} data-testid="seo-title-input" />
              </Field>
              <Field label="Meta Description" hint={`${(seo.meta_description || "").length} chars · aim 70-160`}>
                <Textarea rows={3} value={seo.meta_description || ""} onChange={(e) => set({ meta_description: e.target.value })} placeholder={p.meta_description} data-testid="seo-desc-input" />
              </Field>
              <Field label="Canonical URL"><Input value={seo.canonical_url || ""} onChange={(e) => set({ canonical_url: e.target.value })} placeholder={p.canonical_url} data-testid="seo-canonical-input" /></Field>
              <Field label="Open Graph Title"><Input value={seo.og_title || ""} onChange={(e) => set({ og_title: e.target.value })} placeholder={p.og_title} /></Field>
              <Field label="Open Graph Description"><Textarea rows={2} value={seo.og_description || ""} onChange={(e) => set({ og_description: e.target.value })} placeholder={p.og_description} /></Field>
              <Field label="Open Graph Image URL"><Input value={seo.og_image || ""} onChange={(e) => set({ og_image: e.target.value })} placeholder={p.og_image} /></Field>
              <Field label="Twitter / X Image URL"><Input value={seo.twitter_image || ""} onChange={(e) => set({ twitter_image: e.target.value })} placeholder={p.twitter_image} /></Field>
              <Field label="Focus Keyword (optional)"><Input value={seo.focus_keyword || ""} onChange={(e) => set({ focus_keyword: e.target.value })} placeholder="e.g. rocket fireworks" /></Field>
              <div className="flex gap-6 pt-1">
                <label className="flex items-center gap-2 text-sm"><Switch checked={seo.index !== false} onCheckedChange={(v) => set({ index: v })} data-testid="seo-index-toggle" /> Index (show in search)</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={seo.follow !== false} onCheckedChange={(v) => set({ follow: v })} data-testid="seo-follow-toggle" /> Follow links</label>
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-white/15 p-4 text-sm text-white/60 space-y-2">
              <p className="font-medium text-white/80">Automatic SEO is on.</p>
              <p>Title, description, canonical URL, Open Graph and Twitter tags are generated from your global templates. Turn off "Use Automatic SEO" above to edit any field manually.</p>
              <div className="pt-2 text-xs space-y-1">
                <p><span className="text-white/40">Title:</span> {p.title}</p>
                <p><span className="text-white/40">Description:</span> {p.meta_description}</p>
                <p><span className="text-white/40">URL:</span> {p.canonical_url}</p>
              </div>
            </div>
          )}
        </div>

        {/* Live previews + score checks */}
        <div className="space-y-5">
          {loading && <p className="text-xs text-white/40 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Updating preview…</p>}
          {/* Google */}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50 mb-2 flex items-center gap-2"><Search className="h-3.5 w-3.5" /> Google Search Preview</p>
            <div className="rounded-lg bg-white p-3" data-testid="seo-preview-google">
              <p className="text-[#1a0dab] text-base leading-tight truncate">{g.title || "SEO title will appear here"}</p>
              <p className="text-[#006621] text-xs truncate">{g.url || "https://…"}</p>
              <p className="text-[#545454] text-xs mt-0.5 line-clamp-2">{g.description || "Meta description will appear here."}</p>
            </div>
          </div>
          {/* Facebook */}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50 mb-2 flex items-center gap-2"><Facebook className="h-3.5 w-3.5" /> Facebook / LinkedIn Preview</p>
            <div className="rounded-lg overflow-hidden border border-black/10 bg-white" data-testid="seo-preview-facebook">
              {fb.image ? <img src={fb.image} alt="" className="w-full h-40 object-cover" onError={(e) => (e.currentTarget.style.display = "none")} /> : <div className="w-full h-40 bg-gray-200" />}
              <div className="p-3 bg-[#f2f3f5]">
                <p className="text-[10px] uppercase text-[#606770] truncate">{(fb.url || "").replace(/^https?:\/\//, "")}</p>
                <p className="text-[#1d2129] font-semibold text-sm truncate">{fb.title || "Open Graph title"}</p>
                <p className="text-[#606770] text-xs line-clamp-2">{fb.description || "Open Graph description"}</p>
              </div>
            </div>
          </div>
          {/* WhatsApp */}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50 mb-2 flex items-center gap-2"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp Preview</p>
            <div className="rounded-lg bg-[#dcf8c6] p-2 max-w-sm" data-testid="seo-preview-whatsapp">
              <div className="rounded bg-white overflow-hidden flex">
                <div className="p-2 flex-1 min-w-0">
                  <p className="text-[#111b21] font-medium text-xs truncate">{fb.title || "Title"}</p>
                  <p className="text-[#667781] text-[11px] line-clamp-2">{fb.description || "Description"}</p>
                  <p className="text-[#8696a0] text-[10px] truncate">{(fb.url || "").replace(/^https?:\/\//, "")}</p>
                </div>
                {fb.image && <img src={fb.image} alt="" className="w-16 h-16 object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations / checks */}
      {sc?.checks && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4">
          <p className="text-sm font-medium mb-3">SEO Checks & Recommendations</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {sc.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-xs" data-testid={`seo-check-${c.id}`}>
                <StatusIcon status={c.status} />
                <div>
                  <span className="text-white/80 font-medium">{c.label}</span>
                  {c.message && <span className="text-white/50"> — {c.message}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="seo-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold" /> AI SEO Suggestions</DialogTitle>
            <DialogDescription>Review the AI-generated SEO below. Applying will switch this page to manual SEO so you can fine-tune it.</DialogDescription>
          </DialogHeader>
          {aiSuggest && (
            <div className="space-y-3 text-sm">
              <Sug label="Title" v={aiSuggest.title} />
              <Sug label="Meta Description" v={aiSuggest.meta_description} />
              <Sug label="OG Title" v={aiSuggest.og_title} />
              <Sug label="OG Description" v={aiSuggest.og_description} />
              <Sug label="Image ALT" v={aiSuggest.image_alt} />
              <Sug label="Focus Keyword" v={aiSuggest.focus_keyword} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button className="cf-gold-btn" onClick={applyAi} data-testid="seo-ai-apply-btn">Apply Suggestions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="content" className="mt-4">
          <ContentSeoSection
            entityType={entityType}
            value={seo}
            onChange={onChange}
            entity={entity}
            meta={{ title: p.title, meta_description: p.meta_description, slug: slug || entity.slug, h1: entity.name || entity.title }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-white/60">{label}</Label>
        {hint && <span className="text-[10px] text-white/40">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Sug({ label, v }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
      <p className="text-white/85">{v || <span className="text-white/30">—</span>}</p>
    </div>
  );
}
